/**
 * StripeService — Paiement par carte Stripe (web uniquement).
 *
 * L'app mobile utilise RevenueCat / IAP (Apple, Google). Sur le web où l'IAP
 * n'existe pas, on bascule sur Stripe Checkout (page hébergée Stripe, redirect).
 *
 * Le SDK Stripe côté client n'est PAS nécessaire — on utilise Checkout en
 * mode redirect : POST Lambda → on récupère une URL Stripe → window.location.
 *
 * Endpoints Lambda (cf. aws/lambda/stripe-handler.js) :
 *  - POST /stripe/create-checkout-session  { tier, period } → { url }
 *  - POST /stripe/customer-portal                            → { url }
 *
 * Toutes les méthodes throw si appelées hors web.
 */

import { Platform } from 'react-native';
import { AWS_CONFIG } from '../config/aws_config';
import { getAuthToken } from './authHelpers';

const API_URL = AWS_CONFIG.apiUrl;

function ensureWeb() {
  if (Platform.OS !== 'web' || typeof window === 'undefined') {
    throw new Error('StripeService est web-only. Sur mobile, utilise IAPService (RevenueCat).');
  }
}

async function authHeaders() {
  const token = await getAuthToken();
  if (!token) throw new Error('Non authentifié — connectez-vous pour souscrire.');
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

// ─── Démarrer un paiement Stripe Checkout ──────────────────────
// tier   : 'basic' | 'premium' | 'gold'
// period : 'monthly' | 'yearly'
// → redirige le navigateur vers Stripe (window.location). Au retour Stripe
//   redirige vers /billing/success?session_id=... (cf. useStripeCallback).
export async function startCheckout(tier, period, opts = {}) {
  ensureWeb();
  // v1.12.638 — Reset sessionStorage flags pour la prochaine popup success
  // (sinon flag déjà set → popup ne s'affiche pas au retour)
  try {
    window.sessionStorage?.removeItem('__stripe_success_shown__');
    window.sessionStorage?.removeItem('__stripe_addon_success_shown__');
    window.sessionStorage?.removeItem('__stripe_cancel_shown__');
  } catch {}
  // v1.12.369 — GA4 event: checkout_started (web Stripe)
  try {
    const { trackEvent } = require('./AnalyticsService');
    trackEvent('checkout_started', { tier, period, gateway: 'stripe', platform: 'web', useCreditAmountEur: opts.useCreditAmountEur || 0 });
  } catch {}
  const headers = await authHeaders();

  // v1.12.848 — Crédit Mytenue : opts.useCreditAmountEur (montant à appliquer)
  const payload = { tier, period };
  if (opts.useCreditAmountEur && Number(opts.useCreditAmountEur) > 0) {
    payload.useCreditAmountEur = Number(opts.useCreditAmountEur);
  }
  const res = await fetch(`${API_URL}/stripe/create-checkout-session`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    // v1.12.671 — Parser le JSON pour exposer le code (already_subscribed etc.)
    let data = null;
    try { data = await res.json(); } catch {}
    const detail = data?.detail || data?.message || `HTTP ${res.status}`;
    const err = new Error(detail);
    err.code = data?.message || null;
    err.status = res.status;
    throw err;
  }
  const data = await res.json();
  if (!data?.url) throw new Error('Réponse Stripe invalide (pas d\'URL).');

  // Redirect vers la page Stripe — l'utilisateur reviendra sur /billing/success
  // ou /billing/cancel selon le résultat.
  window.location.href = data.url;
}

// ─── Démarrer un paiement Stripe Checkout pour un add-on one-shot ──
// v1.12.570 — Phase B bundle v3.
// addonId : 'addon_tryons_3' | 'addon_tryons_10' | ... | 'addon_wardrobe_250'
// → redirige le navigateur vers Stripe Checkout (mode 'payment').
//   Au retour : /billing/addon-success?session_id=... ou /billing/cancel.
export async function startAddonCheckout(addonId, opts = {}) {
  ensureWeb();
  try {
    window.sessionStorage?.removeItem('__stripe_success_shown__');
    window.sessionStorage?.removeItem('__stripe_addon_success_shown__');
    window.sessionStorage?.removeItem('__stripe_cancel_shown__');
  } catch {}
  try {
    const { trackEvent } = require('./AnalyticsService');
    trackEvent('checkout_started', { addonId, gateway: 'stripe', kind: 'addon', platform: 'web', useCreditAmountEur: opts.useCreditAmountEur || 0 });
  } catch {}
  const headers = await authHeaders();

  const payload = { addonId };
  if (opts.useCreditAmountEur && Number(opts.useCreditAmountEur) > 0) {
    payload.useCreditAmountEur = Number(opts.useCreditAmountEur);
  }
  const res = await fetch(`${API_URL}/payment/checkout-addon`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`Stripe addon checkout indisponible (${res.status}). ${detail}`);
  }
  const data = await res.json();
  if (!data?.url) throw new Error('Réponse Stripe invalide (pas d\'URL).');
  // v1.12.572 — mémoriser l'addonId pour affichage du message de confirmation
  // au retour sur /billing/addon-success (le webhook insère côté backend).
  try { window.localStorage?.setItem('pending_addon_purchase', addonId); } catch {}
  window.location.href = data.url;
}

// ─── V5.4 : Stripe Checkout pour wardrobe subscription (Plus/Max mensuel) ──
// v1.12.601.
// addonId : 'subscription_wardrobe_plus' | 'subscription_wardrobe_max'
// → mode='subscription' (récurrent mensuel). Refuse Free (403).
export async function startWardrobeSubscriptionCheckout(addonId, opts = {}) {
  ensureWeb();
  try {
    window.sessionStorage?.removeItem('__stripe_success_shown__');
    window.sessionStorage?.removeItem('__stripe_addon_success_shown__');
    window.sessionStorage?.removeItem('__stripe_cancel_shown__');
  } catch {}
  try {
    const { trackEvent } = require('./AnalyticsService');
    trackEvent('checkout_started', { addonId, gateway: 'stripe', kind: 'wardrobe_subscription', platform: 'web', useCreditAmountEur: opts.useCreditAmountEur || 0 });
  } catch {}
  const headers = await authHeaders();

  const payload = { addonId };
  if (opts.useCreditAmountEur && Number(opts.useCreditAmountEur) > 0) {
    payload.useCreditAmountEur = Number(opts.useCreditAmountEur);
  }
  const res = await fetch(`${API_URL}/payment/checkout-wardrobe-subscription`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    if (res.status === 403) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.detail || data.message || 'Réservé aux abonnés Basic+');
    }
    const detail = await res.text().catch(() => '');
    throw new Error(`Stripe wardrobe sub checkout indisponible (${res.status}). ${detail}`);
  }
  const data = await res.json();
  if (!data?.url) throw new Error('Réponse Stripe invalide (pas d\'URL).');
  try { window.localStorage?.setItem('pending_addon_purchase', addonId); } catch {}
  window.location.href = data.url;
}

// ─── v1.12.658 : Change plan d'un user déjà abonné ────────────────
// Règle métier (cf memory single-subscription-rule):
//   - 1 seul abo actif à la fois
//   - Upgrade tier → immédiat avec proration
//   - Downgrade tier → schedule à fin période
//   - Pas de 2ème sub Stripe parallèle (évite double facturation)
//
// Retour: { ok, mode: 'updated'|'scheduled', effectiveAt? }
//   - mode='updated' → tier actif immédiatement (upgrade ou change period)
//   - mode='scheduled' → tier actif à effectiveAt (downgrade)
export async function changeSubscription(tier, period) {
  ensureWeb();
  console.warn('[Stripe] 🔄 changeSubscription(', tier, period, ')');
  const headers = await authHeaders();
  const res = await fetch(`${API_URL}/stripe/change-subscription`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ tier, period }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail = data?.detail || data?.message || `HTTP ${res.status}`;
    // v1.12.669 — Expose le code Stripe (data.message) pour matching frontend.
    // Avant: seul data.detail (texte FR) → impossible de matcher 'already_scheduled_to_this_plan'.
    const err = new Error(detail);
    err.code = data?.message || null;
    err.status = res.status;
    throw err;
  }
  try {
    const { trackEvent } = require('./AnalyticsService');
    trackEvent('subscription_changed', { tier, period, mode: data.mode, gateway: 'stripe' });
  } catch {}
  return data;
}

// v1.12.675 — Preview du montant prélevé par Stripe pour un change
// (proration calculée en live par Stripe). Permet d'afficher au user
// le montant exact AVANT qu'il confirme.
// Retour: { amountDueEur, isCredit, items, isFirstSubscription }
export async function previewChangeSubscription(tier, period) {
  ensureWeb();
  const headers = await authHeaders();
  const res = await fetch(`${API_URL}/stripe/preview-change`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ tier, period }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data?.detail || data?.message || `HTTP ${res.status}`);
    err.code = data?.message || null;
    throw err;
  }
  return data;
}

// v1.12.670 — Annuler un changement de plan programmé (schedule pending).
// Si le pending était un prepaid yearly, l'invoice est refundée.
// Retour: { ok, refunded, releasedScheduleId, wasPrepaid }
export async function cancelPendingChange() {
  ensureWeb();
  console.warn('[Stripe] 🧹 cancelPendingChange()');
  const headers = await authHeaders();
  const res = await fetch(`${API_URL}/stripe/cancel-pending-change`, {
    method: 'POST',
    headers,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data?.detail || data?.message || `HTTP ${res.status}`);
    err.code = data?.message || null;
    throw err;
  }
  return data;
}

// V5.4 : Annuler un addon subscription (cancel_at_period_end)
export async function cancelAddonSubscription(subscriptionId) {
  const headers = await authHeaders();
  const res = await fetch(`${API_URL}/payment/addon-subscriptions/${encodeURIComponent(subscriptionId)}`, {
    method: 'DELETE',
    headers,
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`Annulation échouée (${res.status}). ${detail}`);
  }
  return await res.json();
}

// V5.4 : Lister les wardrobe subscriptions actives
export async function fetchMyWardrobeSubscriptions() {
  const headers = await authHeaders();
  const res = await fetch(`${API_URL}/payment/my-wardrobe-subscriptions`, { headers });
  if (!res.ok) return [];
  return await res.json();
}

// ─── Ouvrir le portail client Stripe (gestion abonnement) ──────
// Renvoie l'utilisateur sur une page hébergée Stripe pour annuler son
// abonnement, mettre à jour sa CB, voir ses factures, etc.
export async function openCustomerPortal() {
  ensureWeb();
  const headers = await authHeaders();

  const res = await fetch(`${API_URL}/stripe/customer-portal`, {
    method: 'POST',
    headers,
  });
  if (!res.ok) {
    if (res.status === 404) {
      throw new Error('Aucun abonnement Stripe trouvé. Souscris d\'abord à un plan.');
    }
    const detail = await res.text().catch(() => '');
    throw new Error(`Portail indisponible (${res.status}). ${detail}`);
  }
  const data = await res.json();
  if (!data?.url) throw new Error('Réponse portail invalide.');
  window.location.href = data.url;
}

// ─── Helper : tier + period depuis un planId ───────────────────
// Mapping id → { tier, period } depuis PREMIUM_PLANS / PREMIUM_PLANS_YEARLY.
//   'basic'         → { tier: 'basic',   period: 'monthly' }
//   'premium_yearly'→ { tier: 'premium', period: 'yearly'  }
export function parsePlanId(planId) {
  if (!planId) return null;
  const yearly = planId.endsWith('_yearly');
  const tier = yearly ? planId.replace(/_yearly$/, '') : planId;
  if (!['basic', 'premium', 'gold'].includes(tier)) return null;
  return { tier, period: yearly ? 'yearly' : 'monthly' };
}

export default { startCheckout, changeSubscription, cancelPendingChange, startAddonCheckout, startWardrobeSubscriptionCheckout, cancelAddonSubscription, fetchMyWardrobeSubscriptions, openCustomerPortal, parsePlanId };
